import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, MapPin, Home, ArrowRight, X } from 'lucide-react';
import { RentualIcon } from '../components/RentualLogo';
import { WizardData, ScoreResult, calculateScore } from '../utils/scoring';
import { formatRut, validateRut } from '../utils/rut';
import { saveLead, trackEvent } from '../utils/tracking';
const initialData: WizardData = {
  income: 0,
  targetRent: 0,
  hasAval: '',
  jobTenure: '',
  dicom: '',
  fullName: '',
  email: '',
  rut: ''
};
const formatCLP = (val: number) => {
  if (!val) return '';
  return '$' + new Intl.NumberFormat('es-CL').format(val);
};
const parseCLP = (val: string) => {
  return parseInt(val.replace(/[^0-9]/g, ''), 10) || 0;
};
// ── Shared props interface ──────────────────────────────────────────
interface ScreenProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  nextScreen: () => void;
  result: ScoreResult | null;
  isLoading: boolean;
  termsAccepted: boolean;
  setTermsAccepted: (v: boolean) => void;
  dataAccepted: boolean;
  setDataAccepted: (v: boolean) => void;
}
// ── Screen 1: Income ────────────────────────────────────────────────
function Screen1({
  data,
  updateData,
  nextScreen
}: ScreenProps) {
  const [inputValue, setInputValue] = useState(data.income ? formatCLP(data.income) : '');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = parseCLP(e.target.value);
    setInputValue(num ? formatCLP(num) : '');
    updateData({
      income: num
    });
  };
  const setQuickValue = (val: number) => {
    setInputValue(formatCLP(val));
    updateData({
      income: val
    });
  };
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-532">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-tight" data-id="element-533">
        ¿Cuánto ganas al mes líquido?
      </h1>

      <div className="mb-8" data-id="element-534">
        <input type="text" inputMode="numeric" placeholder="$0" value={inputValue} onChange={handleChange} className="w-full text-4xl font-bold text-brand-600 border-b-2 border-gray-200 focus:border-brand-600 outline-none py-4 bg-transparent transition-colors placeholder-gray-300" autoFocus data-id="element-535" />
        <p className="text-sm text-gray-500 mt-3" data-id="element-536">
          Tu ingreso define la mayoría de tus opciones de arriendo
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-12" data-id="element-537">
        {[800000, 1200000, 1800000, 2500000].map(val => <button key={val} onClick={() => setQuickValue(val)} className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-brand-600 hover:text-brand-600 transition-colors" data-id="element-538">
            {formatCLP(val)}
          </button>)}
      </div>

      <button onClick={nextScreen} disabled={!data.income || data.income < 100000} className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-200 disabled:text-gray-400 text-white py-4 rounded-xl font-bold transition-all text-lg mt-auto" data-id="element-539">
        Continuar
      </button>
    </div>;
}
// ── Screen 2: Target Rent ───────────────────────────────────────────
function Screen2({
  data,
  updateData,
  nextScreen
}: ScreenProps) {
  const [inputValue, setInputValue] = useState(data.targetRent ? formatCLP(data.targetRent) : '');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = parseCLP(e.target.value);
    setInputValue(num ? formatCLP(num) : '');
    updateData({
      targetRent: num
    });
  };
  const recommended = Math.round(data.income * 0.3);
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-540">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-tight" data-id="element-541">
        ¿Cuánto arriendo estás buscando?
      </h1>

      <div className="mb-8" data-id="element-542">
        <input type="text" inputMode="numeric" placeholder="$0" value={inputValue} onChange={handleChange} className="w-full text-4xl font-bold text-brand-600 border-b-2 border-gray-200 focus:border-brand-600 outline-none py-4 bg-transparent transition-colors placeholder-gray-300" autoFocus data-id="element-543" />
        <p className="text-sm text-gray-500 mt-3" data-id="element-544">
          Lo recomendado es no superar el 30% de tu ingreso líquido
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-12" data-id="element-545">
        <button onClick={() => {
        setInputValue(formatCLP(recommended));
        updateData({
          targetRent: recommended
        });
      }} className="px-4 py-2 rounded-full border border-brand-200 bg-brand-50 text-sm font-medium text-brand-700 hover:bg-brand-100 transition-colors" data-id="element-546">
          Sugerido: {formatCLP(recommended)}
        </button>
      </div>

      <button onClick={nextScreen} disabled={!data.targetRent || data.targetRent < 50000} className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-200 disabled:text-gray-400 text-white py-4 rounded-xl font-bold transition-all text-lg mt-auto" data-id="element-547">
        Continuar
      </button>
    </div>;
}
// ── Screen 3: Aval ──────────────────────────────────────────────────
function Screen3({
  updateData,
  nextScreen
}: ScreenProps) {
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-548">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 leading-tight" data-id="element-549">
        ¿Tienes aval?
      </h1>

      <div className="space-y-4" data-id="element-550">
        {['Sí', 'No'].map(opt => <button key={opt} onClick={() => {
        updateData({
          hasAval: opt
        });
        nextScreen();
      }} className="w-full py-5 px-6 rounded-2xl border-2 border-gray-200 text-xl font-medium text-gray-700 hover:border-brand-600 hover:bg-brand-50 hover:text-brand-700 transition-all text-left" data-id="element-551">
            {opt}
          </button>)}
      </div>
    </div>;
}
// ── Screen 4: Job Tenure ────────────────────────────────────────────
function Screen4({
  updateData,
  nextScreen
}: ScreenProps) {
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-552">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 leading-tight" data-id="element-553">
        ¿Hace cuánto trabajas en tu trabajo actual?
      </h1>

      <div className="space-y-4" data-id="element-554">
        {['Menos de 6 meses', '6 a 12 meses', '1 a 2 años', 'Más de 2 años'].map(opt => <button key={opt} onClick={() => {
        updateData({
          jobTenure: opt
        });
        nextScreen();
      }} className="w-full py-5 px-6 rounded-2xl border-2 border-gray-200 text-lg font-medium text-gray-700 hover:border-brand-600 hover:bg-brand-50 hover:text-brand-700 transition-all text-left" data-id="element-555">
            {opt}
          </button>)}
      </div>
    </div>;
}
// ── Screen 5: DICOM ─────────────────────────────────────────────────
function Screen5({
  updateData,
  nextScreen
}: ScreenProps) {
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-556">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight" data-id="element-557">
        ¿Has tenido problemas financieros (DICOM)?
      </h1>
      <p className="text-gray-500 mb-10" data-id="element-558">
        Esto puede afectar cómo te evalúan los corredores
      </p>

      <div className="space-y-4" data-id="element-559">
        {['No', 'No estoy seguro', 'Sí'].map(opt => <button key={opt} onClick={() => {
        updateData({
          dicom: opt
        });
        nextScreen();
      }} className="w-full py-5 px-6 rounded-2xl border-2 border-gray-200 text-lg font-medium text-gray-700 hover:border-brand-600 hover:bg-brand-50 hover:text-brand-700 transition-all text-left" data-id="element-560">
            {opt}
          </button>)}
      </div>
    </div>;
}
// ── Screen 6: Loading ───────────────────────────────────────────────
function Screen6() {
  return <div className="flex flex-col h-full items-center justify-center max-w-md mx-auto w-full px-6 py-12 text-center" data-id="element-561">
      <div className="w-16 h-16 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin mb-8" data-id="element-562"></div>
      <h2 className="text-2xl font-bold text-gray-900" data-id="element-563">
        Analizando tu perfil...
      </h2>
    </div>;
}
// ── Screen 7: Lock ──────────────────────────────────────────────────
function Screen7({
  nextScreen
}: ScreenProps) {
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12 text-center" data-id="element-564">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" data-id="element-565">
        Tu resultado está listo
      </h1>
      <p className="text-xl text-gray-600 mb-12" data-id="element-566">
        Solo necesitamos verificar tu identidad
      </p>

      <button onClick={nextScreen} className="w-full bg-brand-600 hover:bg-brand-700 text-white py-4 rounded-xl font-bold transition-all text-lg shadow-lg shadow-brand-600/20" data-id="element-567">
        Validar mi perfil
      </button>
    </div>;
}
// ── Screen 8: Identity ──────────────────────────────────────────────
function Screen8({
  data,
  updateData,
  nextScreen,
  isLoading,
  termsAccepted,
  setTermsAccepted,
  dataAccepted,
  setDataAccepted
}: ScreenProps) {
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
  const isRutValid = validateRut(data.rut);
  const isNameValid = data.fullName.trim().length > 3;
  const isComplete = isNameValid && isEmailValid && isRutValid && termsAccepted && dataAccepted;
  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatRut(e.target.value);
    updateData({
      rut: formatted
    });
  };
  return <div className="flex flex-col h-full justify-center max-w-md mx-auto w-full px-6 py-12" data-id="element-568">
      <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-569">
        Valida tu perfil
      </h1>
      <p className="text-sm text-gray-500 mb-8" data-id="element-570">
        Verificamos tus datos para entregarte un diagnóstico real.
      </p>

      <div className="space-y-5 mb-10" data-id="element-571">
        <div data-id="element-572">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-573">
            Nombre completo
          </label>
          <input type="text" name="name" autoComplete="name" value={data.fullName} onChange={e => updateData({
          fullName: e.target.value
        })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all" data-id="element-574" />
        </div>

        <div data-id="element-575">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-576">
            Email
          </label>
          <div className="relative" data-id="element-577">
            <input type="email" name="email" autoComplete="email" value={data.email} onChange={e => updateData({
            email: e.target.value
          })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all" data-id="element-578" />
            {isEmailValid && <CheckCircle2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-579" />}
          </div>
        </div>

        <div data-id="element-580">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-581">
            RUT
          </label>
          <div className="relative" data-id="element-582">
            <input type="text" name="rut" autoComplete="off" value={data.rut} onChange={handleRutChange} maxLength={12} className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all ${data.rut && !isRutValid ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-583" />
            {isRutValid && <CheckCircle2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-584" />}
          </div>
          {data.rut && !isRutValid && <p className="text-xs text-red-500 mt-1" data-id="element-585">RUT inválido</p>}
        </div>

        <div className="pt-4 space-y-4" data-id="element-586">
          <label className="flex items-start gap-3 cursor-pointer group" data-id="element-587">
            <div className="relative flex items-center justify-center mt-0.5" data-id="element-588">
              <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="peer sr-only" data-id="element-589" />
              <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:bg-brand-600 peer-checked:border-brand-600 transition-colors" data-id="element-590"></div>
              <CheckCircle2 size={14} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity" data-id="element-591" />
            </div>
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors" data-id="element-592">
              Acepto términos y política de privacidad
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group" data-id="element-593">
            <div className="relative flex items-center justify-center mt-0.5" data-id="element-594">
              <input type="checkbox" checked={dataAccepted} onChange={e => setDataAccepted(e.target.checked)} className="peer sr-only" data-id="element-595" />
              <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:bg-brand-600 peer-checked:border-brand-600 transition-colors" data-id="element-596"></div>
              <CheckCircle2 size={14} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity" data-id="element-597" />
            </div>
            <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors" data-id="element-598">
              Autorizo el uso de mis datos para evaluar mi perfil
            </span>
          </label>
        </div>
      </div>

      <button onClick={nextScreen} disabled={!isComplete || isLoading} className="w-full bg-brand-600 hover:bg-brand-700 disabled:bg-gray-200 disabled:text-gray-400 text-white py-4 rounded-xl font-bold transition-all text-lg mt-auto flex items-center justify-center" data-id="element-599">
        {isLoading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-600"></div> : 'Ver mi resultado'}
      </button>
    </div>;
}
// ── Screen 9: Result ────────────────────────────────────────────────
function Screen9({
  data,
  result
}: ScreenProps) {
  const [showModal, setShowModal] = useState(false);
  if (!result) return null;
  const getProbColor = (prob: string) => {
    if (prob === 'Alta') return 'text-green-600 bg-green-50 border-green-200';
    if (prob === 'Media') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };
  const handleMejorarPerfil = () => {
    trackEvent({
      event_name: 'click_mejorar_perfil',
      email: data.email,
      rut: data.rut,
      score: result.score,
      probabilidad: result.probability
    });
    setShowModal(true);
  };
  const handleVerPropiedades = () => {
    trackEvent({
      event_name: 'click_ver_propiedades',
      email: data.email,
      rut: data.rut,
      score: result.score,
      probabilidad: result.probability
    });
    setShowModal(true);
  };
  const mockProperties = [{
    id: 1,
    type: 'Departamento',
    commune: 'Providencia',
    price: data.targetRent * 0.9,
    match: 'Alta'
  }, {
    id: 2,
    type: 'Departamento',
    commune: 'Ñuñoa',
    price: data.targetRent * 1.05,
    match: 'Media'
  }, {
    id: 3,
    type: 'Casa',
    commune: 'Santiago',
    price: data.targetRent * 0.8,
    match: 'Alta'
  }];
  return <div className="w-full max-w-2xl mx-auto px-6 py-12 space-y-12" data-id="element-601">
      {/* Header */}
      <div className="text-center" data-id="element-602">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight" data-id="element-603">
          {result.diagnosis}
        </h1>
        <p className="text-lg text-gray-600 mb-8" data-id="element-604">{result.recommendation}</p>
        <div className={`inline-flex items-center px-6 py-2 rounded-full border-2 font-bold text-lg ${getProbColor(result.probability)}`} data-id="element-605">
          {result.probability} probabilidad
        </div>
      </div>

      {/* Actions */}
      <div data-id="element-606">
        <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-607">
          Qué puedes hacer ahora
        </h3>
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100" data-id="element-608">
          <ul className="space-y-4" data-id="element-609">
            {result.actions.map((action, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-610">
                <CheckCircle2 size={20} className="text-brand-600 shrink-0 mt-0.5" data-id="element-611" />
                <span className="text-gray-700 font-medium" data-id="element-612">{action}</span>
              </li>)}
          </ul>
        </div>
      </div>

      {/* Match */}
      <div data-id="element-613">
        <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-614">
          Propiedades compatibles con tu perfil
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-615">
          {mockProperties.map(prop => <div key={prop.id} className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-616">
              <div className={`text-[10px] font-bold px-2 py-1 rounded-md inline-block mb-3 uppercase tracking-wider ${prop.match === 'Alta' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-617">
                {prop.match} compatibilidad
              </div>
              <span className="block text-lg font-bold text-gray-900 mb-2" data-id="element-618">
                {formatCLP(prop.price)}
              </span>
              <div className="flex flex-col gap-1 text-sm text-gray-500" data-id="element-619">
                <div className="flex items-center gap-1.5" data-id="element-620">
                  <Home size={14} data-id="element-621" />
                  <span data-id="element-622">{prop.type}</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-623">
                  <MapPin size={14} data-id="element-624" />
                  <span data-id="element-625">{prop.commune}</span>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-626">
        <button onClick={handleMejorarPerfil} className="flex-1 bg-brand-600 hover:bg-brand-700 text-white py-4 rounded-xl font-bold transition-all text-center text-lg" data-id="element-627">
          Mejorar mi perfil
        </button>
        <button onClick={handleVerPropiedades} className="flex-1 bg-white hover:bg-gray-50 border-2 border-gray-200 text-gray-700 py-4 rounded-xl font-bold transition-all text-center flex items-center justify-center gap-2 text-lg" data-id="element-628">
          Ver propiedades compatibles
          <ArrowRight size={18} data-id="element-629" />
        </button>
      </div>

      {/* Próximamente Modal */}
      <AnimatePresence data-id="element-630">
        {showModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-6" onClick={() => setShowModal(false)} data-id="element-631">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full relative" onClick={e => e.stopPropagation()} data-id="element-632">
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-633">
                <X size={20} data-id="element-634" />
              </button>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-635">
                Próximamente
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-636">
                Estamos construyendo esta funcionalidad. Déjanos tu interés y te
                avisamos cuando esté disponible.
              </p>
              <button onClick={() => setShowModal(false)} className="w-full bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-xl font-semibold transition-colors" data-id="element-637">
                Entendido
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ── Main Wizard Component ───────────────────────────────────────────
export function ScoreWizard() {
  const [screen, setScreen] = useState(1);
  const [data, setData] = useState<WizardData>(initialData);
  const [result, setResult] = useState<ScoreResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dataAccepted, setDataAccepted] = useState(false);
  const updateData = (updates: Partial<WizardData>) => {
    setData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const nextScreen = () => {
    if (screen === 5) {
      setScreen(6);
      setTimeout(() => {
        setScreen(7);
      }, 2000);
    } else if (screen === 8) {
      setIsLoading(true);
      setTimeout(() => {
        const scoreResult = calculateScore(data);
        setResult(scoreResult);
        saveLead({
          nombre: data.fullName,
          email: data.email,
          rut: data.rut,
          score: scoreResult.score,
          probabilidad: scoreResult.probability
        });
        setIsLoading(false);
        setScreen(9);
      }, 1500);
    } else {
      setScreen(prev => Math.min(prev + 1, 9));
    }
    window.scrollTo(0, 0);
  };
  const prevScreen = () => {
    setScreen(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };
  const screenProps: ScreenProps = {
    data,
    updateData,
    nextScreen,
    result,
    isLoading,
    termsAccepted,
    setTermsAccepted,
    dataAccepted,
    setDataAccepted
  };
  const renderScreen = () => {
    switch (screen) {
      case 1:
        return <Screen1 {...screenProps} data-id="element-638" />;
      case 2:
        return <Screen2 {...screenProps} data-id="element-639" />;
      case 3:
        return <Screen3 {...screenProps} data-id="element-640" />;
      case 4:
        return <Screen4 {...screenProps} data-id="element-641" />;
      case 5:
        return <Screen5 {...screenProps} data-id="element-642" />;
      case 6:
        return <Screen6 data-id="element-643" />;
      case 7:
        return <Screen7 {...screenProps} data-id="element-644" />;
      case 8:
        return <Screen8 {...screenProps} data-id="element-645" />;
      case 9:
        return <Screen9 {...screenProps} data-id="element-646" />;
      default:
        return <Screen1 {...screenProps} data-id="element-647" />;
    }
  };
  const showProgress = screen >= 1 && screen <= 5;
  const showBack = screen > 1 && screen <= 5 || screen === 8;
  return <div className="min-h-screen bg-white font-sans selection:bg-brand-100 selection:text-brand-900 flex flex-col" data-id="element-648">
      {/* Minimal Header */}
      <header className="absolute top-0 left-0 right-0 z-50" data-id="element-649">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-650">
          <div className="w-10" data-id="element-651">
            {showBack && <button onClick={prevScreen} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-50" data-id="element-652">
                <ArrowLeft size={24} data-id="element-653" />
              </button>}
          </div>

          {showProgress && <div className="flex gap-2" data-id="element-654">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className={`w-2 h-2 rounded-full transition-colors duration-300 ${i === screen ? 'bg-brand-600' : i < screen ? 'bg-brand-200' : 'bg-gray-200'}`} data-id="element-655" />)}
            </div>}

          <div className="w-10 flex justify-end" data-id="element-656">
            {screen === 9 && <Link to="/" className="text-gray-400 hover:text-gray-900" data-id="element-657">
                <RentualIcon size={24} data-id="element-658" />
              </Link>}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col relative pt-20" data-id="element-659">
        <AnimatePresence mode="wait" data-id="element-660">
          <motion.div key={screen} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="flex-1 flex flex-col" data-id="element-661">
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>;
}